import { useMemo, useState } from "react";
import type { VisitRow, VisitStatus } from "@/lib/database.types";
import { Button } from "./Button";
import { StatusBadge } from "./StatusBadge";
import { AddToCalendar } from "./AddToCalendar";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_LETTERS = ["S","M","T","W","T","F","S"];

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

interface Cell {
  date: Date;
  inMonth: boolean;
}

function buildGrid(monthStart: Date): Cell[] {
  const firstWeekday = monthStart.getDay();
  const start = new Date(monthStart);
  start.setDate(start.getDate() - firstWeekday);
  const cells: Cell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({ date: d, inMonth: d.getMonth() === monthStart.getMonth() });
  }
  return cells;
}

const badgeFor: Record<VisitStatus, { tone: "active" | "muted" | "warn"; label: string }> = {
  scheduled: { tone: "active", label: "Scheduled" },
  completed: { tone: "muted",  label: "Completed" },
  cancelled: { tone: "warn",   label: "Cancelled" },
  no_show:   { tone: "warn",   label: "No-show" },
};

interface Props {
  visits: VisitRow[];
  onBook: () => void;
}

export function AppointmentCalendar({ visits, onBook }: Props) {
  const today = useMemo(() => new Date(), []);
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);

  const visitsByDate = useMemo(() => {
    const m = new Map<string, VisitRow[]>();
    for (const v of visits) {
      if (!v.scheduled_at) continue;
      const key = isoDate(new Date(v.scheduled_at));
      const arr = m.get(key) ?? [];
      arr.push(v);
      m.set(key, arr);
    }
    return m;
  }, [visits]);

  const cells = useMemo(() => buildGrid(viewMonth), [viewMonth]);

  const todayIso = isoDate(today);
  const selectedIso = selectedDate ? isoDate(selectedDate) : null;

  const dayVisits = useMemo(() => {
    if (!selectedIso) return [];
    return (visitsByDate.get(selectedIso) ?? []).slice().sort((a, b) =>
      (a.scheduled_at ?? "").localeCompare(b.scheduled_at ?? ""),
    );
  }, [visitsByDate, selectedIso]);

  const upcoming = useMemo(() => {
    return visits
      .filter((v) => v.scheduled_at && new Date(v.scheduled_at) >= today && v.status === "scheduled")
      .slice()
      .sort((a, b) => (a.scheduled_at ?? "").localeCompare(b.scheduled_at ?? ""))
      .slice(0, 3);
  }, [visits, today]);

  const rightHeading = selectedDate
    ? selectedDate.toLocaleDateString(undefined, {
        weekday: "long", month: "long", day: "numeric",
      })
    : "Upcoming";
  const rightList: VisitRow[] = selectedDate ? dayVisits : upcoming;
  const rightEmpty = selectedDate
    ? "No appointments on this day."
    : "No upcoming appointments.";

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-6">
      {/* Calendar */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
            className="w-7 h-7 rounded-md text-fh-gray hover:bg-fh-cream hover:text-fh-ink text-[16px]"
            aria-label="Previous month"
          >‹</button>
          <h3 className="font-serif text-[15px] text-fh-ink">
            {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </h3>
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            className="w-7 h-7 rounded-md text-fh-gray hover:bg-fh-cream hover:text-fh-ink text-[16px]"
            aria-label="Next month"
          >›</button>
        </div>

        <div className="grid grid-cols-7 mb-1">
          {DAY_LETTERS.map((d, i) => (
            <div key={i} className="text-center text-[11px] font-semibold text-fh-gray py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, i) => {
            const iso = isoDate(cell.date);
            const isToday = iso === todayIso;
            const isSelected = iso === selectedIso;
            const hasVisit = visitsByDate.has(iso);
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedDate(cell.date)}
                className={[
                  "relative aspect-square rounded-md text-[13px] font-medium transition",
                  cell.inMonth ? "text-fh-ink" : "text-fh-gray-light",
                  isSelected
                    ? "bg-fh-green text-white"
                    : isToday
                      ? "ring-1 ring-fh-green text-fh-green-dark"
                      : "hover:bg-fh-cream",
                ].join(" ")}
                aria-label={cell.date.toDateString()}
                aria-pressed={isSelected}
              >
                <span>{cell.date.getDate()}</span>
                {hasVisit && (
                  <span
                    className={[
                      "absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full",
                      isSelected ? "bg-white" : "bg-fh-green",
                    ].join(" ")}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setSelectedDate(null)}
          className="mt-3 text-[12px] font-semibold text-fh-green hover:text-fh-green-dark"
        >
          Show upcoming
        </button>
      </div>

      {/* Selected day / upcoming list */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[14px] font-semibold text-fh-ink truncate">{rightHeading}</h4>
          <Button size="sm" onClick={onBook}>+ Book</Button>
        </div>

        {rightList.length === 0 ? (
          <p className="text-[13px] text-fh-gray">{rightEmpty}</p>
        ) : (
          <div className="flex flex-col gap-2.5">
            {rightList.map((v) => (
              <VisitMini key={v.id} visit={v} today={today} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VisitMini({ visit, today }: { visit: VisitRow; today: Date }) {
  const when = visit.scheduled_at ? new Date(visit.scheduled_at) : null;
  const isPast = when ? when < today : false;
  const status = visit.status;
  const badge = badgeFor[status];
  const canJoin = status === "scheduled" && !isPast && !!visit.video_link;

  return (
    <div className="rounded-md border border-fh-border bg-white px-3 py-2.5">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[13.5px] font-semibold text-fh-ink truncate">
          {visit.provider?.full_name ?? "Provider TBD"}
        </div>
        {badge && <StatusBadge label={badge.label} tone={badge.tone} />}
      </div>
      <div className="text-[12.5px] text-fh-gray">
        {when
          ? when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
          : "Time TBD"}
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        {canJoin && (
          <a
            href={visit.video_link!}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-[12.5px] font-semibold text-fh-green hover:text-fh-green-dark"
          >
            Join video visit →
          </a>
        )}
        <AddToCalendar visit={visit} size="sm" />
      </div>
    </div>
  );
}
